package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * canvas 元素 AST（M11.6 / M11.7 Stage 1）。
 *
 * canvas 作为叶子元素与 box/text 并列出现在 root 或 box 的子位置，
 * 由 CanvasBlockParser 解析、CanvasEmitter 转换为 UiCanvasData。
 *
 * 字段约束：
 *  - [width] / [height] 必填，承载任意编译期表达式（含 props.x、运算等）。
 *    Emitter 会判别是否为 NumberLit 常量走常量快路径；否则编为 ExprBlob。
 *  - [durationMs] 可选；未声明时由 Emitter 填入默认值。当表达式为 NumberLit
 *    时走常量路径，否则编为 ExprBlob。
 *  - [loopMode] 当前仍为字符串字面量。
 *  - [attrs] 是 canvas 元素自身的通用 prop（如 background / translateX），
 *    与 box 节点的 prop 同语义。
 *  - [drawBlock] 必填，用于每帧重绘。
 *  - [onceBlock] 可选，仅在首帧执行（MVP 保留字段，后续版本启用）。
 */
data class CanvasAst(
    val id: String,
    val width: ExprAst,
    val height: ExprAst,
    val durationMs: ExprAst?,
    val loopMode: String?,
    /** canvas 元素自身属性（与 box 元素 prop 同语义）。 */
    val attrs: List<ElementAst.PropAssign>,
    val drawBlock: DrawAst,
    val onceBlock: DrawAst?,
    override val position: SourcePosition,
) : AstNode

/**
 * `draw { ... }` / `once { ... }` 块的 AST。
 */
data class DrawAst(
    val instructions: List<DrawInstructionAst>,
    override val position: SourcePosition,
) : AstNode

/**
 * draw 块内允许的指令家族：图元调用、val 声明、for 循环、if/else。
 */
sealed interface DrawInstructionAst : AstNode {
    /** 图元调用：circle(...) / rect(...) / dot(...) / ... */
    data class Primitive(
        val name: String,
        val args: List<ExprAst>,
        override val position: SourcePosition,
    ) : DrawInstructionAst

    /** `val name = expr` 声明；name 在后续指令中可作为表达式变量引用。 */
    data class ValAssign(
        val name: String,
        val value: ExprAst,
        override val position: SourcePosition,
    ) : DrawInstructionAst

    /**
     * `for (i in 0 until N) { body }`。
     *
     * from/to 必须是编译期整数常量，由 Parser 固化为 [Int]。
     */
    data class For(
        val varName: String,
        val from: Int,
        val to: Int,
        val body: List<DrawInstructionAst>,
        override val position: SourcePosition,
    ) : DrawInstructionAst

    /** `if (cond) { then } [else { elseBranch }]`；无 else 时 [elseBranch] 为空列表。 */
    data class IfElse(
        val condition: ExprAst,
        val thenBranch: List<DrawInstructionAst>,
        val elseBranch: List<DrawInstructionAst>,
        override val position: SourcePosition,
    ) : DrawInstructionAst
}
